import { holdsAnywhere } from '@carii/casl-abilities';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { FormField, Input, Select, Textarea } from '../../components/ui/FormField.js';
import type { CarFuel } from '../../components/ui/FuelBars.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { useAbility } from '../../context/AbilityContext.js';
import { useAuth } from '../../context/AuthContext.js';
import { useBackNavigation } from '../../hooks/useBackNavigation.js';
import { useSaveMutation } from '../../hooks/useSaveMutation.js';
import { api } from '../../services/api.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';
import { BUFFER_CHOICES, bufferLabel } from '../../utils/bufferMinutes.js';
import {
  type Currency,
  type RateCardTemplate,
  buildRateCardOptions,
  isMultiRateCard,
  storedRateCardIds,
} from '../../utils/rateCardOptions.js';
import { CarCartrackCard } from './CarCartrackCard.js';
import { CarImmobiliserCard } from './CarImmobiliserCard.js';
import { type CarPhotoImage, CarPhotosPanel } from './CarPhotos/CarPhotosPanel.js';
import { CarPositionCard } from './CarPositionCard.js';
import { CarRatingCard } from './CarRatingCard.js';
import { CarSalesSettingsCard } from './CarSalesSettingsCard.js';
import { CarSupplierCard } from './CarSupplierCard.js';
import { HostsPicker } from './HostsPicker.js';
import { BrandModelFields } from './components/BrandModelFields.js';

export interface Taxon {
  _id: string;
  name: string;
  brand_id?: string;
}

/**
 * A pickup place as `GET /locations` returns it (056 R3). The three descriptive fields are the
 * ones a car copies: the customer car page renders them, so omitting them creates a car with a
 * blank Pickup block (FR-006a). `state_name`, `address_line_2` and `postal_code` are absent from
 * this payload, so a car leaves them unset rather than inventing them.
 *
 * 218 (#314): no longer carries `latitude`/`longitude` — a car has no position of its own to
 * pre-position, so nothing here needs a place's own coordinate either.
 */
interface PickupPlace {
  _id: string;
  name: string;
  city_name?: string;
  country_name?: string;
  address_line_1?: string;
}

interface SupplierOption {
  _id: string;
  company_name: string;
}

/** 408: the platform's rule (`backend/src/modules/cars/fuel.ts`): 2–16 whole bars else 8, a bar counts once full. */
function fuelPreview(percent: number, typed: string): string {
  const n = Number(typed);
  const bars = typed.trim() !== '' && Number.isInteger(n) && n >= 2 && n <= 16 ? n : 8;
  return `${percent}% shows as ${Math.min(bars, Math.floor((percent * bars) / 100))} of ${bars} bars`;
}

interface CarData {
  brand_id?: string;
  brand_name?: string;
  model_id?: string | null;
  model_name?: string;
  category_id?: string | null;
  feature_ids?: string[];
  year?: number | string;
  plate_number?: string;
  vin?: string;
  tracking_url?: string;
  /** 405 (#315): the car's Cartrack vehicle (staff-only); written only by the Cartrack card, never by this form. */
  cartrack_vehicle_id?: number | null;
  cartrack_linked_at?: string | null;
  /** 339 (#939): staff-only; the customer sees it in their booking's window. */
  lockbox_code?: string;
  /** 386 (#1278): the car's own gap between rentals; null = the platform's. */
  buffer_minutes?: number | null;
  /** 408 (#1434): the bars on the car's fuel gauge (2–16); null = 8. */
  fuel_gauge_bars?: number | null;
  /** 408: the car's fuel now, from Cartrack (read only; absent when there is none). */
  fuel?: CarFuel;
  color?: string;
  transmission?: 'auto' | 'manual';
  fuel_type?: 'petrol' | 'diesel' | 'electric' | 'hybrid';
  seat_capacity?: number | string;
  description?: string;
  operating_hours?: { start: string; end: string } | null;
  /**
   * 056: the car's pickup location **as stored**.
   *
   * 218 (#314): no `geo` any more — a car has no position of its own. A stored record from
   * before this feature may still carry an old `geo` value; it is never read here, so its
   * presence or shape cannot matter.
   */
  location?: {
    location_id?: string;
    city_name?: string;
    country_name?: string;
    address_line_1?: string;
  };
  /**
   * 060: the three keys the rate-card control reads.
   *
   * `supplier_id` was NOT declared here before, though the staff payload has always carried it —
   * without it the edit-mode filter compares every rate card against `undefined` and offers none.
   * `listing_status` drives the FR-015 warning; `pricing_template_ids` is the field being edited,
   * in priority order.
   */
  supplier_id?: string;
  /** 360: the supplier's name, which `GET /cars/:id` joins. */
  supplier?: { id: string; company_name: string } | null;
  /** 363: the car's reviews, as the customer sees them; null when nobody has reviewed it. */
  rating?: { average: number; count: number } | null;
  listing_status?: string;
  pricing_template_ids?: string[];
  /** 098: the car's stored photo set, as `GET /cars/:id` returns it. */
  images?: CarPhotoImage[];
  /** 377 (#1233): the car's hosts (several), as `GET /cars/:id` publishes them. */
  host_membership_ids?: string[];
}

/** 303: a Host membership as `GET /memberships` returns it (`memberships/serializer.ts`). */
interface HostMembershipOption {
  id: string;
  user: { id: string; name: string; email: string } | null;
  can_see_sales?: boolean;
}

/**
 * The pickup location as the form SENDS it (contract C2). Sent whole or not at all (INV-4): the
 * platform validates the group only when supplied, so an edit that leaves the location alone
 * omits the key and the stored value is untouched (FR-008).
 *
 * 218 (#314): no `geo` — `location_id` is the only thing that determines pickup now.
 */
interface CarLocationPayload {
  location_id: string;
  city_name?: string;
  country_name?: string;
  address_line_1?: string;
}

type CarBody = Omit<Partial<CarData>, 'location'> & {
  location?: CarLocationPayload;
  supplier_id?: string;
  host_membership_ids?: string[];
};

export function CarFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const goBack = useBackNavigation('/cars');
  const queryClient = useQueryClient();
  const isEdit = Boolean(id);
  /**
   * 156 (#11): `/cars/new?from=<id>` starts a NEW car from an existing one's specification.
   *
   * Deliberately a query parameter on the create route rather than a route of its own, so
   * `isEdit` stays false and every create-only behaviour below — the POST, the supplier choice,
   * the null-clearing rules — applies unchanged. The only difference is where the defaults come
   * from.
   */
  const [searchParams] = useSearchParams();
  const copyFromId = isEdit ? null : searchParams.get('from');

  // Controlled taxonomy selections (cascade needs the current brand).
  const [brandId, setBrandId] = useState('');
  const [modelId, setModelId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [featureIds, setFeatureIds] = useState<string[]>([]);
  // 056: the pickup place. Controlled page state, not a FormData field — a select read through
  // FormData would be lost whenever an unrelated change re-rendered the form (FR-011, INV-6).
  const [locationId, setLocationId] = useState('');
  // FR-006: what the operator is told is missing, before a request is spent finding out.
  const [locationError, setLocationError] = useState('');
  /**
   * Whether the operator changed the pickup place or the pin during THIS edit (FR-008).
   *
   * Prefilling an existing car does not count — it is set by the two controls only. An edit
   * that leaves the location alone must send no `location` key at all, because the platform
   * validates the group only when it is supplied. That is also what keeps a car stored with an
   * unusable point editable (FR-009): demanding a valid pin on every save would make such a
   * car unsaveable until its location was repaired, replacing one failure with another.
   */
  const [locationTouched, setLocationTouched] = useState(false);
  /**
   * 056, found live: `supplier_id` is the OTHER field this form never offered.
   *
   * A car must belong to a supplier, and `POST /cars` stamps one from the caller ONLY when the
   * caller has their own (`cars/routes.ts:131`, 034 FR-006). An administrator has none, so their
   * create reached the Car model with no supplier and failed as an unmapped mongoose
   * ValidationError — a 500 for what is really a missing field. Exactly the defect class of the
   * missing location control, on the same form, and the reason Create Car still could not
   * succeed for the account operators actually use.
   *
   * So the choice is offered only to a caller who owns no supplier. A provider is never shown
   * it: the route overrides whatever they send, and offering the choice would imply a power they
   * do not have (FR-013).
   */
  const { activeSupplierId: ownSupplierId } = useAuth();
  const choosesSupplier = !ownSupplierId && !isEdit;
  const [supplierId, setSupplierId] = useState('');
  const [supplierError, setSupplierError] = useState('');
  /**
   * 303: which Host this car is assigned to, if any. Shown only to a caller who can actually
   * assign one (`assign-host` — Supplier/Admin, never Host, even on their own assigned car;
   * spec FR-002) — a Host viewing their own car's edit form (they hold `update Car` on it) must
   * not see a control implying a power they do not have.
   */
  const ability = useAbility();
  const canAssignHost = ability.can('assign-host', 'Car');
  // 386 (#1278): a car's own gap is a platform rule, set only by a platform-wide car editor (the server refuses others).
  const canSetBuffer = holdsAnywhere(ability as never, 'update', 'Car');
  const platformBuffer = useQuery({
    queryKey: ['platform-config'],
    queryFn: () => api.get<{ booking_buffer_minutes?: number }>('/platform-config'),
    enabled: canSetBuffer,
  });
  const platformGap = platformBuffer.data?.booking_buffer_minutes;
  // 377 (#1233): the car's hosts, any number.
  const [hostIds, setHostIds] = useState<string[]>([]);
  // #1146: the host travels on save only when staff changed it, like the location below. A save that did not touch the
  // host can then never wipe the assignment, nor re-send a host who has since left (which the platform refuses).
  const [hostTouched, setHostTouched] = useState(false);
  /**
   * 060 (H9): which rate card this car is rented at.
   *
   * Nothing in the panel could set this before, so a car added here carried no price and H7's
   * Publish refused it with no way for the operator to fix the cause.
   *
   * Controlled state rather than a FormData field, because the offered options depend on the
   * supplier and the FR-015 warning depends on the current choice. A car storing MORE than one rate
   * card is edited read-only (FR-016): a single-select save would `$set` a one-element array over a
   * priority list and silently destroy the fall-through cards.
   */
  const [rateCardId, setRateCardId] = useState('');

  // 156: the same read serves both — editing a car and copying one. `sourceId` is what is being
  // read; `isEdit` still decides what happens on save.
  const sourceId = id ?? copyFromId ?? undefined;
  const { data: car } = useQuery({
    queryKey: RESOURCE_KEYS.car.one(sourceId as string),
    queryFn: () => api.get<CarData>(`/cars/${sourceId}`),
    enabled: Boolean(sourceId),
  });

  const { data: categories } = useQuery({
    queryKey: RESOURCE_KEYS.carCategory.list,
    queryFn: () => api.get<Taxon[]>('/car-categories?status=active'),
  });
  const { data: features } = useQuery({
    queryKey: RESOURCE_KEYS.carFeature.list,
    queryFn: () => api.get<Taxon[]>('/car-features?status=active'),
  });
  // 056: the pickup places, for the selector and for pre-positioning the pin. One request,
  // cached by TanStack Query — the form adds no per-keystroke traffic.
  const { data: locations } = useQuery({
    queryKey: RESOURCE_KEYS.location.list,
    queryFn: () => api.get<{ items: PickupPlace[] }>('/locations'),
  });
  // Only fetched when the caller actually has a choice to make.
  const { data: suppliers } = useQuery({
    queryKey: RESOURCE_KEYS.supplier.list,
    queryFn: () => api.get<{ items: SupplierOption[] }>('/suppliers'),
    enabled: choosesSupplier,
  });
  // 303: this car's own supplier — on edit, the car's stored one; on create, whichever the
  // caller either owns or has just chosen above (mirrors `rateCardSupplierId`'s own reasoning).
  const carOwnSupplierId = isEdit ? (car?.supplier_id ?? '') : (ownSupplierId ?? supplierId);
  const { data: hostMemberships } = useQuery({
    queryKey: ['memberships', 'host-picker', carOwnSupplierId],
    queryFn: () =>
      api.get<{ items: HostMembershipOption[] }>(
        `/memberships?supplier_id=${carOwnSupplierId}&role=Host&employment_status=active`,
      ),
    enabled: canAssignHost && Boolean(carOwnSupplierId),
  });

  /**
   * 060 (FR-004, D3): whose rate cards may be offered — the CAR's supplier, never "all".
   *
   * On edit that is the supplier the car belongs to; on create, the caller's own if they have one,
   * else the supplier they chose above. `GET /pricing-templates` is supplier-scoped for a provider
   * but returns EVERY supplier's rate cards to an administrator (research R2), so the filter is
   * applied client-side and offers nothing until the supplier is known (FR-005).
   */
  const rateCardSupplierId = isEdit ? (car?.supplier_id ?? '') : (ownSupplierId ?? supplierId);
  const storedRateCards = storedRateCardIds(car?.pricing_template_ids);
  const multiRateCard = isMultiRateCard(car?.pricing_template_ids);

  const { data: rateCards } = useQuery({
    queryKey: RESOURCE_KEYS.pricingTemplate.list,
    queryFn: () => api.get<{ items: RateCardTemplate[] }>('/pricing-templates'),
    enabled: Boolean(rateCardSupplierId),
  });
  const { data: currencies } = useQuery({
    queryKey: ['currencies'],
    queryFn: () => api.get<Currency[]>('/currencies'),
    enabled: Boolean(rateCardSupplierId),
  });

  // 408 (#1434): the gauge's bars, typed (controlled so the preview follows). '' = the default, 8.
  const [gaugeBars, setGaugeBars] = useState('');

  useEffect(() => {
    if (!car) return;
    // 408: copied too: the bars describe the model's dashboard, not one physical car.
    setGaugeBars(car.fuel_gauge_bars == null ? '' : String(car.fuel_gauge_bars));
    if (car.brand_id) setBrandId(car.brand_id);
    if (car.model_id) setModelId(car.model_id);
    if (car.category_id) setCategoryId(car.category_id);
    if (car.feature_ids) setFeatureIds(car.feature_ids);
    // 056 FR-007: show where the car currently is. Assigned directly rather than through
    // `selectPlace`, so prefilling never counts as the operator changing the location (FR-008).
    if (car.location?.location_id) setLocationId(car.location.location_id);
    // 060 FR-003: show the rate card the car carries. ONLY for the single-card case, because
    // picking index 0 of a priority list would misrepresent it as one choice.
    //
    // `=== 1` rather than `?.length` is intent, not a tested branch: mutation testing showed
    // relaxing it changes nothing observable today, since a multi-card car renders no select
    // (FR-016) and sends no key. It matters the moment someone adds the ordered control, so it
    // stays — recorded as deliberate rather than left to look like coverage.
    const onlyRateCard =
      car.pricing_template_ids?.length === 1 ? car.pricing_template_ids[0] : undefined;
    if (onlyRateCard) setRateCardId(onlyRateCard);
    // 156 (#11): the new car belongs to the same supplier as the one being copied. Only when
    // copying, and only when the caller is actually offered the choice — an operator with their
    // own supplier has it stamped by the platform, and on edit this control is not rendered at
    // all. Found live: everything else prefilled while Supplier sat on "Select a supplier…".
    if (copyFromId && car.supplier_id) setSupplierId(car.supplier_id);
    // 303: prefilled only on edit — a NEW car (including one copied from another) starts
    // unassigned, matching every other car's own starting state (spec FR-008's own "no backfill,
    // no invented default" rule applied at create time too).
    // 360: re-read after a change of supplier, which clears the host unless it set one of the new supplier's.
    if (isEdit) setHostIds(car.host_membership_ids ?? []);
  }, [car, copyFromId, isEdit]);

  // 161: the fleet list AND this car's own cache entry. Only the list was invalidated before, so
  // reopening this form inside the 30-second stale window served the values from before the save.
  // `id` is undefined when copying (156), which is a create and has no record key yet.
  const mutation = useSaveMutation<CarBody>({
    resource: 'car',
    id,
    mutationFn: (data) => (isEdit ? api.patch(`/cars/${id}`, data) : api.post('/cars', data)),
    onSaved: () => navigate('/cars'),
  });

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const {
      operating_hours_start: start,
      operating_hours_end: end,
      ...rest
    } = Object.fromEntries(fd.entries());
    const data = rest as unknown as CarBody;
    // 386: the gap select sends '' for "use the platform value": null clears a car's own gap; a new car sends nothing.
    if ('buffer_minutes' in rest) {
      const raw = String(rest.buffer_minutes);
      const target = data as unknown as Record<string, unknown>;
      if (raw !== '') target.buffer_minutes = Number(raw);
      else if (isEdit) target.buffer_minutes = null;
      else Reflect.deleteProperty(target, 'buffer_minutes');
    }
    // 408: '' means "the default (8)": null clears a car's own bars; a new car sends nothing.
    if ('fuel_gauge_bars' in rest) {
      const raw = String(rest.fuel_gauge_bars).trim();
      const target = data as unknown as Record<string, unknown>;
      if (raw !== '') target.fuel_gauge_bars = Number(raw);
      else if (isEdit) target.fuel_gauge_bars = null;
      else Reflect.deleteProperty(target, 'fuel_gauge_bars');
    }
    // Structured taxonomy overrides free-text; backend stamps display names.
    // On edit, an empty optional select sends null so the backend clears the
    // id and its stale denormalized display name (brand is required — never null).
    if (brandId) data.brand_id = brandId;
    if (modelId) data.model_id = modelId;
    else if (isEdit) data.model_id = null;
    if (categoryId) data.category_id = categoryId;
    else if (isEdit) data.category_id = null;
    data.feature_ids = featureIds;
    // 060 (FR-001, FR-002, FR-016): one rate card, as a one-element array — or `[]` to clear it,
    // which the platform turns into a withdrawal when the car is published (FR-012). A multi-card
    // car sends NO key at all, so its stored priority list is untouched by this form.
    if (!multiRateCard) data.pricing_template_ids = rateCardId ? [rateCardId] : [];
    if (start && end) data.operating_hours = { start: String(start), end: String(end) };
    else if (isEdit) data.operating_hours = null;
    // The location travels whole or not at all (INV-4, contract C3): always on create, and on
    // edit only when the operator actually changed it. A group without the descriptive fields
    // would silently blank a shopper's pickup details.
    if (!isEdit || locationTouched) {
      // FR-006: name the missing piece before spending a round-trip on it. The platform's own
      // refusal stays the backstop (C5) — `mutation.error` is still rendered below, so a client
      // rule that ever drifts from the platform's cannot produce an unexplained failure.
      const location = buildLocation();
      if (!location) {
        setLocationError('Choose a pickup place for this car.');
        return;
      }
      data.location = location;
    }
    if (choosesSupplier) {
      if (!supplierId) {
        setSupplierError('Choose the supplier that owns this car.');
        return;
      }
      data.supplier_id = supplierId;
    }
    // 303: only sent when the control is actually rendered — a Host viewing their own car's form
    // (no `assign-host`) must never send this key at all, even accidentally.
    if (canAssignHost && hostTouched) {
      data.host_membership_ids = hostIds;
    }
    setSupplierError('');
    setLocationError('');
    void mutation.save(data);
  }

  /**
   * The `location` group the platform expects (contract C2), or `undefined`.
   *
   * All-or-nothing by construction (INV-4): sending the id without the descriptive fields would
   * blank a shopper's Pickup block. There is no partial form of this object.
   *
   * 218 (#314): no `geo` any more — a pickup place reference is the whole thing.
   *
   * The descriptive fields are copied from the chosen place and only where the place actually
   * carries them — `state_name`, `address_line_2` and `postal_code` exist on a car but are
   * absent from `GET /locations`, so they stay unset rather than being invented (R3), and a
   * place recording no street address contributes no key rather than an empty string, which
   * would render as a stray separator on the customer car page.
   */
  function buildLocation() {
    if (!locationId) return undefined;
    const place = locations?.items?.find((l) => l._id === locationId);
    // The backend applies this with `$set: { location }` (cars/service.ts:282), which REPLACES
    // the whole subdocument — so a detail missing here is a detail ERASED from the stored car,
    // not one left alone. When the referenced place is gone from the places list (deleted or
    // deactivated) there is nothing to copy from, so fall back to what the car already carries:
    // re-choosing it must not wipe the city and street address a shopper reads.
    const details = place ?? (locationId === car?.location?.location_id ? car.location : undefined);
    return {
      location_id: locationId,
      ...(details?.city_name ? { city_name: details.city_name } : {}),
      ...(details?.country_name ? { country_name: details.country_name } : {}),
      ...(details?.address_line_1 ? { address_line_1: details.address_line_1 } : {}),
    };
  }

  /** 056 FR-003 / 218 (#314): choosing a pickup place is the whole action now — no pin to move. */
  function selectPlace(id: string) {
    setLocationTouched(true);
    setLocationId(id);
    // Withdraw any complaint the moment it stops being true. Found in the browser: the message
    // used to survive until the next submit, so the form asked for a pickup place while showing
    // the one just chosen.
    setLocationError('');
  }

  function toggleFeature(fid: string) {
    setFeatureIds((ids) => (ids.includes(fid) ? ids.filter((x) => x !== fid) : [...ids, fid]));
  }

  return (
    <div>
      <PageHeader
        title={isEdit ? 'Edit Car' : 'Add Car'}
        description={isEdit ? 'Update car details' : 'Create a new car listing'}
        fallback="/cars"
      />
      {/* 156 (#11): what this form was started from, and what was deliberately NOT carried over.
          Without it an operator sees a form that is mysteriously half-filled and cannot tell
          whether the empty plate is a copy rule or a lost value. */}
      {copyFromId && (
        <div data-testid="copying-from" className="mb-4 text-sm text-steel">
          Copying details from{' '}
          <Link to={`/cars/${copyFromId}/edit`} className="text-primary underline">
            {[car?.brand_name, car?.model_name].filter(Boolean).join(' ') || 'another car'}
          </Link>
          . Plate number and VIN are left blank — they identify this specific vehicle.
        </div>
      )}
      {/* 360 (#1147): the car's supplier, its change (administrators) and its history. Its own card, outside the form:
          the general save never changes the supplier. */}
      {isEdit && id && (
        <div className="mb-6 max-w-2xl space-y-4">
          <CarSupplierCard
            carId={id}
            supplier={car?.supplier}
            onMoved={() =>
              queryClient.invalidateQueries({ queryKey: RESOURCE_KEYS.car.one(id as string) })
            }
          />
          {/* 363 (#1090, design 2i): where this car stands in the customer search. */}
          <CarRatingCard carId={id} rating={car?.rating} />
          {/* 405 (#315, mock-up C1–C6): where the car is now (administrators and its hosts; the platform decides), and
              which Cartrack vehicle it carries (administrators only). Outside the form: the general save never
              writes the link. */}
          <CarPositionCard carId={id} trackingUrl={car?.tracking_url} fuel={car?.fuel} />
          <CarCartrackCard
            carId={id}
            car={car}
            onChanged={() =>
              queryClient.invalidateQueries({ queryKey: RESOURCE_KEYS.car.one(id as string) })
            }
          />
          {/* 419 (#1529, mock-up sections 1–3): immobilise or release through Cartrack (the platform decides who). */}
          <CarImmobiliserCard carId={id} plateNumber={car?.plate_number} />
        </div>
      )}
      <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
        <Card>
          <h2 className="mb-4 text-sm font-semibold text-ink">Vehicle details</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {choosesSupplier && (
              <FormField label="Supplier" name="supplier_id" required error={supplierError}>
                <Select
                  id="supplier_id"
                  value={supplierId}
                  onChange={(e) => {
                    setSupplierId((e.target as HTMLSelectElement).value);
                    setSupplierError('');
                  }}
                >
                  <option value="">Select a supplier…</option>
                  {suppliers?.items?.map((sup) => (
                    <option key={sup._id} value={sup._id}>
                      {sup.company_name}
                    </option>
                  ))}
                </Select>
              </FormField>
            )}
            <BrandModelFields
              brandId={brandId}
              modelId={modelId}
              onBrandChange={setBrandId}
              onModelChange={setModelId}
            />
            <FormField label="Category" name="category_id">
              <Select
                id="category_id"
                value={categoryId}
                onChange={(e) => setCategoryId((e.target as HTMLSelectElement).value)}
              >
                <option value="">Uncategorized</option>
                {categories?.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField label="Year" name="year" required>
              <Input
                id="year"
                type="number"
                name="year"
                min="1990"
                max="2030"
                defaultValue={car?.year ?? ''}
              />
            </FormField>
            <FormField label="Plate Number" name="plate_number" required>
              {/* 156 (#11): empty when copying. A plate identifies one physical car — carrying it
                  over would either be refused by the platform or create a second record claiming
                  to be the same vehicle. */}
              <Input
                id="plate_number"
                name="plate_number"
                defaultValue={copyFromId ? '' : (car?.plate_number ?? '')}
              />
            </FormField>
            <FormField label="VIN" name="vin">
              {/* 156 (#11): empty when copying, for the same reason as the plate. */}
              <Input id="vin" name="vin" defaultValue={copyFromId ? '' : (car?.vin ?? '')} />
            </FormField>
            <FormField label="GPS Tracker URL" name="tracking_url">
              {/* 195 (#13/#16): a third-party live-location share link. Empty when copying, same
                  reason as VIN and the plate — it names one physical tracking device, not the
                  car's specification. */}
              <Input
                id="tracking_url"
                name="tracking_url"
                type="url"
                placeholder="https://…"
                defaultValue={copyFromId ? '' : (car?.tracking_url ?? '')}
              />
            </FormField>
            <FormField
              label="Lockbox code"
              name="lockbox_code"
              hint="Letters, digits, spaces and hyphens, up to 20. The customer sees it from 15 minutes before pickup until the return time."
            >
              {/* 339 (#939): the code of the box holding this car's key. Empty when copying, same
                  reason as the tracker URL: it belongs to one physical box. */}
              <Input
                id="lockbox_code"
                name="lockbox_code"
                maxLength={20}
                autoComplete="off"
                defaultValue={copyFromId ? '' : (car?.lockbox_code ?? '')}
              />
            </FormField>
            <FormField
              label="Fuel gauge bars"
              name="fuel_gauge_bars"
              hint="How many bars the dashboard shows when full (2–16). Leave empty for 8."
            >
              {/* 408 (#1434, mock-up screen 6): the customer sees the fuel as bars on this gauge. */}
              <Input
                id="fuel_gauge_bars"
                name="fuel_gauge_bars"
                type="number"
                inputMode="numeric"
                min={2}
                max={16}
                step={1}
                placeholder="8"
                value={gaugeBars}
                onChange={(e) => setGaugeBars(e.currentTarget.value)}
              />
              {car?.fuel && (
                <p data-testid="fuel-bars-preview" className="mt-1 text-xs text-steel">
                  {fuelPreview(car.fuel.percent, gaugeBars)}
                </p>
              )}
            </FormField>
            {canSetBuffer && (
              <FormField
                label="Gap between rentals"
                name="buffer_minutes"
                hint={
                  'Overrides the platform gap for this car only. "Use platform value" follows Settings when it changes.'
                }
              >
                {/* Remounted once the car arrives: an uncontrolled select ignores a later defaultValue. */}
                <Select
                  key={car ? 'car' : 'loading'}
                  id="buffer_minutes"
                  name="buffer_minutes"
                  defaultValue={car?.buffer_minutes == null ? '' : String(car.buffer_minutes)}
                >
                  <option value="">
                    {platformGap == null
                      ? 'Use platform value'
                      : `Use platform value (${bufferLabel(platformGap)})`}
                  </option>
                  {BUFFER_CHOICES.map((c) => (
                    <option key={c.value} value={String(c.value)}>
                      {c.label}
                    </option>
                  ))}
                </Select>
              </FormField>
            )}
            <FormField label="Color" name="color">
              <Input id="color" name="color" defaultValue={car?.color ?? ''} />
            </FormField>
          </div>
        </Card>

        <Card>
          <h2 className="mb-4 text-sm font-semibold text-ink">Pickup location</h2>
          {/* 218 (#314): the map/pin that used to sit here is removed — it showed a static,
              hand-placed position that looked like the car's real location and was not.
              Nothing on the platform read it. The Pickup Place chosen below is now the only
              thing that determines where a pickup happens. */}
          <FormField label="Pickup Place" name="pickup_location_id" required error={locationError}>
            <Select
              id="pickup_location_id"
              value={locationId}
              onChange={(e) => selectPlace((e.target as HTMLSelectElement).value)}
            >
              <option value="">Select a pickup place…</option>
              {/* `items?.` and not `items.` — a payload without the key must leave the
                  selector empty, never crash the whole form on render. */}
              {locations?.items?.map((l) => (
                <option key={l._id} value={l._id}>
                  {l.name}
                </option>
              ))}
            </Select>
          </FormField>
        </Card>

        {/* 303: Supplier/Admin only — a Host viewing their own car's edit form (they hold `update
            Car`, just not `assign-host`) must not see a control implying a power they lack. */}
        {canAssignHost && (
          <Card>
            <h2 className="mb-4 text-sm font-semibold text-ink">Hosts</h2>
            <HostsPicker
              label="Assigned hosts"
              value={hostIds}
              onChange={(ids) => {
                setHostIds(ids);
                setHostTouched(true);
              }}
              options={(hostMemberships?.items ?? []).map((m) => ({
                id: m.id,
                name: m.user?.name || m.user?.email || 'Unnamed',
                can_see_sales: m.can_see_sales,
              }))}
            />
          </Card>
        )}

        {/* 060 (H9): placed above Specifications because this is the field that decides whether the
            car can ever be shown to a customer — H7's Publish refuses a car with no resolvable rate. */}
        <Card>
          <h2 className="mb-4 text-sm font-semibold text-ink">Rate card</h2>
          {multiRateCard ? (
            <div className="space-y-2 text-sm">
              <p className="text-ink">
                This car uses {storedRateCards.length} rate cards in priority order.
              </p>
              <p className="text-steel">
                Changing them needs an ordered control, which this form does not have yet, so they
                are left exactly as they are.
              </p>
            </div>
          ) : (
            <FormField
              label="Rate card"
              name="rate_card"
              hint="The rate customers see, and what a booking is charged. A car with no rate card cannot be published."
            >
              <Select
                id="rate_card"
                value={rateCardId}
                onChange={(e) => setRateCardId((e.target as HTMLSelectElement).value)}
              >
                <option value="">— none —</option>
                {buildRateCardOptions(
                  rateCards?.items ?? [],
                  currencies ?? [],
                  rateCardSupplierId,
                ).map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </FormField>
          )}
          {/* FR-015, disclosed BEFORE saving: the platform withdraws the listing in the same write,
              so the operator must be able to read that consequence rather than discover it. */}
          {!multiRateCard && !rateCardId && car?.listing_status === 'published' && (
            <p className="mt-3 text-sm text-amber-600">
              This car is published. Saving with no rate card will also unpublish it, so customers
              stop seeing it.
            </p>
          )}
        </Card>

        <Card>
          <h2 className="mb-4 text-sm font-semibold text-ink">Specifications</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label="Transmission" name="transmission" required>
              <Select
                id="transmission"
                name="transmission"
                defaultValue={car?.transmission ?? 'auto'}
              >
                <option value="auto">Automatic</option>
                <option value="manual">Manual</option>
              </Select>
            </FormField>
            <FormField label="Fuel Type" name="fuel_type" required>
              <Select id="fuel_type" name="fuel_type" defaultValue={car?.fuel_type ?? 'petrol'}>
                <option value="petrol">Petrol</option>
                <option value="diesel">Diesel</option>
                <option value="electric">Electric</option>
                <option value="hybrid">Hybrid</option>
              </Select>
            </FormField>
            <FormField label="Seats" name="seat_capacity" required>
              <Input
                id="seat_capacity"
                type="number"
                name="seat_capacity"
                min="1"
                max="20"
                defaultValue={car?.seat_capacity ?? 5}
              />
            </FormField>
          </div>
        </Card>

        <Card>
          <h2 className="mb-4 text-sm font-semibold text-ink">Features</h2>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {features?.length ? (
              features.map((f) => (
                <label key={f._id} className="flex items-center gap-2 text-sm text-ink">
                  <input
                    type="checkbox"
                    checked={featureIds.includes(f._id)}
                    onChange={() => toggleFeature(f._id)}
                    className="rounded border-hairline text-primary focus:ring-primary"
                  />
                  {f.name}
                </label>
              ))
            ) : (
              <p className="text-sm text-steel">No features defined.</p>
            )}
          </div>
        </Card>

        <Card>
          <h2 className="mb-4 text-sm font-semibold text-ink">Operating hours (Malaysia time)</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label="Opens At" name="operating_hours_start">
              <Input
                type="time"
                id="operating_hours_start"
                name="operating_hours_start"
                defaultValue={car?.operating_hours?.start ?? ''}
              />
            </FormField>
            <FormField label="Closes At" name="operating_hours_end">
              <Input
                type="time"
                id="operating_hours_end"
                name="operating_hours_end"
                defaultValue={car?.operating_hours?.end ?? ''}
              />
            </FormField>
          </div>
        </Card>

        <Card>
          <h2 className="mb-4 text-sm font-semibold text-ink">Description</h2>
          <FormField label="Description" name="description">
            <Textarea id="description" name="description" defaultValue={car?.description ?? ''} />
          </FormField>
        </Card>

        {/* 098: a car needs an id before it can hold photos, so this is edit-only. */}
        {isEdit && id && (
          <Card>
            <h2 className="mb-4 text-sm font-semibold text-ink">Photos</h2>
            <CarPhotosPanel
              carId={id}
              images={car?.images ?? []}
              onChange={() =>
                queryClient.invalidateQueries({ queryKey: RESOURCE_KEYS.car.one(id as string) })
              }
            />
          </Card>
        )}

        {mutation.error instanceof Error && (
          <p className="text-sm text-red-600">{mutation.error.message}</p>
        )}

        <div className="flex gap-3">
          <Button type="submit" loading={mutation.isPending}>
            {isEdit ? 'Save Changes' : 'Create Car'}
          </Button>
          <Button type="button" variant="ghost" onClick={goBack}>
            Cancel
          </Button>
        </div>
      </form>
      {/* 342 (#933): its own card and save, outside the form — the general car update never carries these. */}
      {isEdit && id && (
        <div className="mt-6 max-w-2xl">
          <CarSalesSettingsCard carId={id} />
        </div>
      )}
    </div>
  );
}
